import { createSelector } from "reselect"
import { chain, findIndex, first, get, pullAt, isEmpty, size } from "lodash"
import { getMissingDataFiles, modelSupportsComposites } from "selectors/missingDataFiles"
import { getPortfolios, getLastViewed, getBenchmarksLookup, getPortfolioBenchmark } from "selectors/portfolios"
import { getModelSupportedCurrencies, getModelFactorsClassification, getModelFactors } from "selectors/modelMetadata"
import { getDates } from "./dates"
import { getModels } from "./model"
import { computeFullBenchmark, VIEW_TYPES } from "helpers/base"
import { ONE_YEAR } from "helpers/dates"
import { getMostUsedOrDefaultModelId } from "helpers/models"
import { getUserPreference } from "helpers/userPreferences"
import { unsupportedCurrencyWarning } from "helpers/currency"
import { getModels as getRawModels } from "resources/models"

export const getPortfolio = (state) => get(state, "global.data.portfolio.data")
export const getPortfolioName = (state) => get(state, "global.data.portfolio.data.name")
export const getPortfolioLive = (state) => get(state, "global.data.portfolio.data.live")
export const getPortfolioType = (state) => get(state, "global.data.portfolioModelMeta.data.type")
export const getViewType = state => get(state, "modules.portfolioRoot.viewType")
export const getDate = state => get(state, "modules.portfolioRoot.date")
export const getFavoriteFactors = state => get(state, "modules.portfolioRoot.favoriteFactors")
export const getShowNotHeld = state => get(state, "modules.portfolioRoot.showNotHeld", false)
export const getPortfolioId = state => get(state, "global.data.portfolio.data.id")
export const getRawSelectedViews = state => get(state, "modules.portfolioRoot.selectedViews")
export const getStyleExposureAggregation = state => get(state, "modules.portfolioRoot.styleAggregation")
export const getIsExpanded = state => get(state, "modules.portfolioRoot.expand", false)
export const getPreferenceValues = state => get(state, "global.data.portfolioUserPreferences.data")
export const getPeriodFromState = state => get(state, "modules.portfolioRoot.period")

export const getPeriodFromPreferences = createSelector(
  [getPreferenceValues],
  (preferenceValues) => {
    const preferencePeriod = getUserPreference(preferenceValues, "lastSelectedPeriod")
    return isEmpty(preferencePeriod) ? ONE_YEAR : preferencePeriod
  },
)

export const getPeriod = createSelector(
  [getPeriodFromState, getPeriodFromPreferences],
  (periodFromState, periodFromPreferences) => {
    if (!isEmpty(periodFromState)) {
      return periodFromState
    }
    return periodFromPreferences || ONE_YEAR
  },
)

export const getIsActive = createSelector(
  [getViewType], viewType => viewType === VIEW_TYPES.ACTIVE,
)

const getRawSelectedModelId = state => get(state, "modules.portfolioRoot.selectedModel")
const getPortfolioDefaultModelId = state => get(state, "global.data.portfolio.data.modelId")
const getAccountCurrency = state => get(state, "global.selected.accountMeta.defaultCurrency")

export const getNoRestrictionModels = createSelector(
  [getPortfolioDefaultModelId],
  (defaultPortfolioModelId) => [defaultPortfolioModelId],
)

export const getPreferenceModelId = createSelector(
  [getPreferenceValues],
  preferences => getUserPreference(preferences, "modelId"),
)

export const getSelectedModelId = createSelector(
  [getPortfolioDefaultModelId, getPreferenceModelId, getRawSelectedModelId, getModels, getRawModels, getPortfolios],
  (defaultPortfolioModelId, preferenceModelId, selectedModelId, models, rawModels, portfolios) => {
    const computedModelId = selectedModelId || preferenceModelId

    if (computedModelId && models[computedModelId]) {
      return computedModelId
    }

    if (models[defaultPortfolioModelId]) {
      return defaultPortfolioModelId
    }

    return getMostUsedOrDefaultModelId(portfolios, rawModels)
  },
)

export const getMustAggregateFactors = createSelector(
  [getModelFactorsClassification],
  (factorsClassification) => !isEmpty(factorsClassification),
)

export const getSelectedModelName = createSelector(
  [getModels, getSelectedModelId],
  (models, modelId) => get(models, [modelId, "name"]),
)

export const getPreferenceViewType = createSelector(
  [getPreferenceValues],
  preferences => getUserPreference(preferences, "viewType"),
)

export const getPortfolioHasData = createSelector(
  [getDates],
  portfolioDates => size(portfolioDates) > 0)

export const getFullPortfolioBenchmark = createSelector(
  [getPortfolioBenchmark, getBenchmarksLookup, getPortfolioType],
  computeFullBenchmark,
)

const getPath = () => {
  const parts = window.location.pathname.split("/")
  parts[3] = "{portfolioId}"
  return parts.join("/")
}

export function getModelShortDescription(modelId) {
  if (!modelId) {
    return ""
  }

  if (modelId.startsWith("QES")) {
    // "QES-<seg>": skip the 4-char "QES-" prefix, then take the 2-char segment
    return `QES - ${modelId.substring(4, 6)}`
  }

  const upper = modelId.toUpperCase()
  if (upper.startsWith("OP")) {
    // "OP-<seg>": skip the 3-char "OP-" prefix, then take the 2-char segment
    return `OP - ${upper.substring(3, 5)}`
  }

  return `${modelId.substring(0, 2)} - ${modelId.substring(2, 4)}`
}

export const getLatestPortfolios = createSelector(
  [getPortfolios, getLastViewed, getPortfolioId, getPath],
  (portfolios, lastViewed, currentId, path) => {
    portfolios = portfolios.slice() // creates a copy

    let last = chain(lastViewed)
      .map(item => item.id)
      .map(portfolioId => {
        const index = findIndex(portfolios, p => p.id === portfolioId)
        if (index > -1) {
          return first(pullAt(portfolios, index))
        } else {
          return null
        }
      })
      .filter(portfolio => portfolio !== null && portfolio.id !== currentId)
      .take(5)
      .value()

    if (last.length < 5) {
      const rest = chain(portfolios)
        .sortBy("name")
        .take(5 - last.length)
        .value()

      last = last.concat(rest)
    }

    return chain(last)
      .map(portfolio => ({
        id: portfolio.id,
        name: portfolio.name,
        model: getModelShortDescription(portfolio.modelId),
        to: path.replace("{portfolioId}", portfolio.shortId || portfolio.id),
      }))
      .sortBy("name")
      .value()
  },
)

export const getModelSupportsComposites = createSelector(
  [getMissingDataFiles, getSelectedModelId],
  modelSupportsComposites,
)

const getPortfolioCurrency = createSelector(
  [getPortfolio],
  portfolio => get(portfolio, "currencyOverride"),
)

const getCurrencyOverride = createSelector(
  [getAccountCurrency, getPortfolioCurrency],
  (accountCurrency, portfolioCurrency) => portfolioCurrency || accountCurrency,
)

export const getPortfolioFxHedgeEnabled = createSelector(
  [getPortfolio],
  portfolio => get(portfolio, "fxHedgeEnabled"),
)

export const getUnsupportedCurrencyWarning = createSelector(
  [getModelSupportedCurrencies, getAccountCurrency, getPortfolioCurrency, getPortfolioFxHedgeEnabled, getModelFactors],
  unsupportedCurrencyWarning,
)

export const getCurrency = createSelector(
  [getCurrencyOverride, getUnsupportedCurrencyWarning],
  (selectedCurrency, unsupportedCurrencyWarning) => unsupportedCurrencyWarning ? null : selectedCurrency,
)
